import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Your note is on its way — thank you for saying hello. 🌿");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="contact-page">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="font-caveat text-3xl" style={{ color: "#FF6B9D" }}>knock knock</span>
          <h1 className="font-fredoka text-5xl" style={{ color: "#2D3055" }}>Say hello</h1>
          <p className="mt-4 text-lg" style={{ color: "#4A4E6D" }}>
            I love hearing from small readers, big readers, teachers, librarians, and anyone
            with a favorite bedtime story. Send a note — I read every single one.
          </p>

          <div className="mt-8 space-y-4">
            <a href="mailto:hello@mkbunchbooks.com" className="warm-card p-5 flex items-start gap-4 hover:-translate-y-1 transition-transform" data-testid="contact-email-link">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: "#FF6B9D", color: "#fff" }}>
                <Mail size={20} />
              </div>
              <div>
                <p className="font-fredoka" style={{ color: "#2D3055" }}>Email me directly</p>
                <p className="text-sm font-nunito mt-0.5" style={{ color: "#FF6B9D" }}>hello@mkbunchbooks.com</p>
              </div>
            </a>
            <div className="warm-card p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: "#FFD93D", color: "#2D3055" }}>
                <Mail size={20} />
              </div>
              <div>
                <p className="font-fredoka" style={{ color: "#2D3055" }}>Send a letter</p>
                <p className="text-sm font-nunito" style={{ color: "#4A4E6D" }}>Use the form → I'll write back as soon as I can.</p>
              </div>
            </div>
            <div className="warm-card p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: "#6EC4E8", color: "#fff" }}>
                <MessageCircle size={20} />
              </div>
              <div>
                <p className="font-fredoka" style={{ color: "#2D3055" }}>School & library visits</p>
                <p className="text-sm font-nunito" style={{ color: "#4A4E6D" }}>Mention it in your note — I'd love to visit.</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 warm-card p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-fredoka text-sm block mb-2" style={{ color: "#2D3055" }}>Your name</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                className="w-full rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 font-nunito"
                style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="font-fredoka text-sm block mb-2" style={{ color: "#2D3055" }}>Your email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className="w-full rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 font-nunito"
                style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <div>
            <label className="font-fredoka text-sm block mb-2" style={{ color: "#2D3055" }}>Subject (optional)</label>
            <input
              value={form.subject}
              onChange={update("subject")}
              className="w-full rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 font-nunito"
              style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
              data-testid="contact-subject-input"
            />
          </div>
          <div>
            <label className="font-fredoka text-sm block mb-2" style={{ color: "#2D3055" }}>Your note</label>
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={update("message")}
              className="w-full rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 font-nunito resize-none"
              style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
              data-testid="contact-message-input"
            />
          </div>
          <button disabled={loading} className="btn-terracotta w-full justify-center" data-testid="contact-submit-btn">
            <Send size={16} /> {loading ? "Sending…" : "Send your note"}
          </button>
        </form>
      </div>
    </div>
  );
}
